import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import {
	SitePartReferencesDataGridApi
} from "src/api/generated/erp/parts/sitePart/api/sitePartReferencesDataGridApi.ts";
import { enumColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getPartRevisionStateLabels } from "src/api/generated/erp/db/types/enums/partRevisionState.ts";
import { PartRevisionLineView } from "src/api/generated/erp/db/types/tables/partRevisionLineView.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faSitemap } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import {
	SitePartRevisionProductStructureView
} from "src/components/views/erp/parts/siteParts/bom/SitePartRevisionProductStructureView.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SitePartReferencesDataGridProps {
	partId: number;
}

export const SitePartReferencesDataGrid = ({ partId }: SitePartReferencesDataGridProps) => {
	const { openDialog } = useGenericDialog();
	return (
		<ClientSideDataGridModel
			gridId={"1C6F3A86A0FBEA7D"}
			fetchData={SitePartReferencesDataGridApi.fetchSitePartReferences}
			getRows={(data) => data}
			getRowId={(row) => row.partRevisionLineId}
			initialParams={{
				partId: partId,
			}}
			render={({ dataGridProps, onlySelectedRow }) => {
				return (
					<FormCrudDataGrid<PartRevisionLineView>
						disableMultipleRowSelection
						columns={[
							textColumn({
								field: "parentPartNo",
								headerName: i18n.t("part"),
							}),
							textColumn({
								field: "parentPartDescription_1",
								headerName: i18n.t("description_1"),
								width: 200,
							}),
							textColumn({
								field: "parentPartDescription_2",
								headerName: i18n.t("description_2"),
								width: 200,
							}),
							textColumn({
								field: "revisionNumber",
								headerName: i18n.t("revision"),
							}),
							enumColumn({
								field: "revisionState",
								headerName: i18n.t("revision_state"),
								enumLabels: getPartRevisionStateLabels(),
							}),
							textColumn({
								field: "referenceNo",
								headerName: i18n.t("reference_no"),
							}),
						]}
						actionBarComponents={
							<>
								<AavoButton
									label={i18n.t("product_structure")}
									icon={faSitemap}
									variant={"outlined"}
									disabled={onlySelectedRow == null}
									onClick={() => {
										if (onlySelectedRow == null) return;
										openDialog(({ onDataDirtyStateChanged }) => ({
											title: i18n.t("product_structure"),
											agentContext: {
												key: defineAgentUiContextKey("erp.parts.sitePartReferenceProductStructure.dialog"),
												title: i18n.t("product_structure"),
												data: {
													partId,
													partRevisionId: onlySelectedRow.partRevisionId,
												},
											},
											content: (
												<SitePartRevisionProductStructureView
													partRevisionId={onlySelectedRow.partRevisionId}
													dataDirtyStateChanged={onDataDirtyStateChanged}
												/>
											),
										}));
									}}
								/>
							</>
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.parts.sitePartReferences.grid"),
							data: { partId },
							rowFields: ["partRevisionLineId", "partRevisionId", "parentPartId", "partId"],
							selectedRowPathSegment: (row) => `${row.parentPartNo} ${i18n.t("revision")} ${row.revisionNumber}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
